import { ToggleGroup, ToggleGroupItem } from '@mdvip/shared/components/ui/toggle-group';
import { cn } from '@mdvip/shared/lib/utils';
import { useTranslation } from 'react-i18next';
import type { AlphaSelection, DatasetState, MemberDatasetView } from '@/hooks/member-views/useMemberDataset';
import type { MemberRowData } from '@/lib/member-views/memberDataset.types';

export interface AlphaBarProps<TRow extends MemberRowData> {
  alpha: MemberDatasetView<TRow>['alpha'];
  state: DatasetState;
}

/**
 * 44px targets; the selected letter is filled with the brand colour rather than the default pressed
 * tint. The letters share the row's width and sit flush: with a gap, 28 targets outgrow the page
 * column at desktop widths and the last few scroll out of sight.
 */
const ITEM =
  'min-h-11 min-w-11 data-pressed:bg-primary data-pressed:font-semibold data-pressed:text-primary-foreground data-pressed:hover:bg-primary';

/**
 * AlphaBar — filters the view by the first letter of the last name: All, A–Z, and "#" for names
 * that don't start with a letter.
 *
 * A letter no patient falls under is disabled rather than hidden, so the row keeps its shape. The
 * whole bar is disabled while the view loads, and hidden if it failed. It scrolls sideways where
 * 28 targets don't fit.
 */
export function AlphaBar<TRow extends MemberRowData>({ alpha, state }: AlphaBarProps<TRow>) {
  const { t } = useTranslation('providers');

  if (state === 'error') return null;
  const loading = state === 'loading';

  return (
    // A fieldset's default min-width is its content, which would stop the row scrolling sideways.
    // `relative` makes the row the containing block for the letters' absolutely positioned sr-only
    // labels: otherwise they escape the scroll clip and widen the whole page past the viewport.
    <fieldset
      aria-label={t('memberViews.alpha.label')}
      className="relative -mx-4 min-w-0 overflow-x-auto px-4 [scrollbar-width:none]"
    >
      <ToggleGroup
        size="sm"
        value={[alpha.selected]}
        // Pressing the selected letter again unpresses it, which reads as "back to all".
        onValueChange={(values) => alpha.onSelect((values[0] as AlphaSelection | undefined) ?? 'all')}
        className="flex w-max min-w-full gap-0"
      >
        <ToggleGroupItem value="all" disabled={loading} className={cn(ITEM, 'flex-none px-4')}>
          {t('memberViews.alpha.all')}
        </ToggleGroupItem>
        {alpha.letters.map(({ letter, enabled }) => (
          <ToggleGroupItem key={letter} value={letter} disabled={loading || !enabled} className={ITEM}>
            <span aria-hidden="true">{letter}</span>
            <span className="sr-only">
              {t('memberViews.alpha.letterLabel', { letter, isOther: String(letter === '#') })}
            </span>
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </fieldset>
  );
}
